import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';

import { defineMessages, useIntl } from 'react-intl';
import { Page } from '@empiriecom/mybuy-frontend-api/backend-mybuy-checkout/v1/models/Page';
import { useRouter } from 'next/navigation';
import { BypassBlock } from '@packages/shared/src/ui/BypassBlock/BypassBlock';
import { defineStepper } from '@packages/shared/src/ui/Stepper/Stepper';

const messages = defineMessages({
  basket: {
    id: 'ProgressBar.basket',
    defaultMessage: 'Warenkorb',
  },
  login: {
    id: 'ProgressBar.login',
    defaultMessage: 'Anmeldung',
  },
  delivery: {
    id: 'ProgressBar.delivery',
    defaultMessage: 'Lieferung',
  },
  payment: {
    id: 'ProgressBar.payment',
    defaultMessage: 'Zahlung',
  },
  checkorder: {
    id: 'ProgressBar.checkorder',
    defaultMessage: 'Prüfen & Bestellen',
  },
  bypassHeadline: {
    id: 'ProgressBar.Bypass.Headline',
    defaultMessage: 'Bestellnavigation überspringen',
  },
});

export const ProgressBar = ({
  activeStep,
  saveAndNavigate,
}: {
  activeStep: number;
  saveAndNavigate?: (targetPage: Page) => Promise<any>;
}) => {
  const { formatMessage, locale } = useIntl();
  const router = useRouter();
  const {
    order: { paths },
  } = useConfig();
  const { loggedIn } = useLoginState();

  const { Stepper } = defineStepper(
    {
      id: 'step-1',
      title: formatMessage(messages.basket),
      icon: <span className="icon-emp-cart size-4" />,
      localizedPath: getLocalizedPath(paths.basket, locale),
    },
    {
      id: 'step-2',
      title: formatMessage(messages.login),
      icon: loggedIn ? (
        <span className="icon-emp-login-opened size-4" />
      ) : (
        <span className="icon-emp-login-closed size-4" />
      ),
      localizedPath: getLocalizedPath(paths.login, locale),
    },
    {
      id: 'step-3',
      title: formatMessage(messages.delivery),
      icon: <span className="icon-emp-delivery size-4" />,
      localizedPath: getLocalizedPath(paths.delivery, locale),
    },
    {
      id: 'step-4',
      title: formatMessage(messages.payment),
      icon: <span className="icon-emp-billing size-4" />,
      localizedPath: getLocalizedPath(paths.payment, locale),
    },
    {
      id: 'step-5',
      title: formatMessage(messages.checkorder),
      icon: <span className="icon-emp-check-order size-4" />,
      localizedPath: getLocalizedPath(paths.checkandorder, locale),
    },
  );

  const onClickHandler = (path: string) => {
    // todo implement saving data when we need it for checkout steps
    saveAndNavigate?.(Page.Basket);
    router.push(path);
    return undefined;
  };

  return (
    <div className="w-full px-2 pt-2 pb-1.5 md:px-0 md:pb-[1.805rem]">
      <BypassBlock buttonText={formatMessage(messages.bypassHeadline)} skipScrollIntoView>
        <Stepper.Provider
          labelOrientation="vertical"
          initialStep={`step-${activeStep}` as 'step-1'}
        >
          {({ stepper }) => (
            <Stepper.Navigation>
              {stepper.state.all.map((step) => (
                <Stepper.Step
                  key={step.id}
                  of={step.id}
                  onClick={() => onClickHandler(step.localizedPath)}
                  icon={step.icon}
                >
                  <Stepper.Title onClick={() => onClickHandler(step.localizedPath)}>
                    {step.title}
                  </Stepper.Title>
                </Stepper.Step>
              ))}
            </Stepper.Navigation>
          )}
        </Stepper.Provider>
      </BypassBlock>
    </div>
  );
};
